import Vue from "vue";
import axios from "axios";
import { reactive, toRefs, provide, inject, computed } from "@vue/composition-api";
import { actions as appActions, state as appState } from "../../store/modules/app";
import { actions as taskActions, state as taskState } from "../../store/modules/task";
import router from "../../router";
import { parseSysDateTime, calcDateDiff } from "@/plugins/formatting";
import {PUBLIC_MESSAGE_TYPES} from "@/helpers/constants";

const PROTOCOL = process.env.VUE_APP_WS_PROTOCOL ? process.env.VUE_APP_WS_PROTOCOL : "";
const HOST = process.env.VUE_APP_HOST ? process.env.VUE_APP_HOST : "";
const BASE_URL = `${PROTOCOL}://${HOST}`;

var notification_ws = null;

export const state = reactive({
    list: [],
    notifications_log_list: [],
    new_notifications_count: 0,
    map: new Map(),
    reconnect_counter: 0,
    hideNotificationsUntil: null,
    currentTime: new Date(),
    latestNotifications: [],
    latestStickyNotifications: []
});

setInterval(() => {
    state.currentTime = new Date()
}, 1000)

let connectWs = function () {
    notification_ws = new WebSocket(`${BASE_URL}/ws/notifications`);

    notification_ws.onopen = function () {
        console.log("Notifications WebSocket: connected");
        state.reconnect_counter = 0;
    };

    notification_ws.onmessage = function (ev) {
        const message = JSON.parse(ev.data);
        console.log("Notifications WebSocket: ", message);

        const existingNotificationIds = state.list.map(x => x.id)
        const newNotifications = message.notifications.filter(x => {
            return !existingNotificationIds.includes(x.id) && (!getters.isStickyNotification(x) || (state.hideNotificationsUntil && state.hideNotificationsUntil > state.currentTime)) && !x.is_alerted
        })
        state.latestNotifications = state.latestNotifications.concat(newNotifications)
        state.latestStickyNotifications = message.sticky_notifications
        state.list = message.notifications;
        state.new_notifications_count = message.new_notifications_count;

        const allNewNotifications = message.notifications.filter(x => {
            return !existingNotificationIds.includes(x.id)
        })

        for (const notification of allNewNotifications) {
            if (notification.activity &&
                notification.related_type === 'task' &&
                notification.is_seen === 0) {
                const taskIndex = taskState.tasks_list.findIndex(x => x.id === notification.related_id)
                if (taskIndex >= 0) {
                    if (notification.activity.type === 'task_comment') {
                        if (!taskState.tasks_list[taskIndex].unread_internal_comments) {
                            taskState.tasks_list[taskIndex].unread_internal_comments = 1
                        } else {
                            taskState.tasks_list[taskIndex].unread_internal_comments += 1
                        }
                    } else if (PUBLIC_MESSAGE_TYPES.includes(notification.activity.type)) {
                        if (!taskState.tasks_list[taskIndex].unread_public_messages) {
                            taskState.tasks_list[taskIndex].unread_public_messages = 1
                        } else {
                            taskState.tasks_list[taskIndex].unread_public_messages += 1
                        }
                    }
                }
            }
        }
    };

    notification_ws.onclose = function () {
        console.log("Notifications WebSocket: connection lost");
        if (state.reconnect_counter >= appState.reconnect_max_tries) {
            appActions.showGlobalConnectionLost();
        } else {
            state.reconnect_counter++;
            setTimeout(function () {
                console.log("Notifications WebSocket: reconnecting...({0})".format(state.reconnect_counter));
                connectWs();
            }, appState.reconnect_wait);
        }
    };
};

export const actions = {
    connectWs: connectWs,
    markNotificationsSeen(notification_id_list) {
        return new Promise(function (resolve, reject) {
            axios
                .post(Vue.config.url_prefix + "/api/notifications/mark_as_seen/", { notification_id_list: notification_id_list })
                .then(resp => {
                    if (notification_id_list) {
                        for (const id of notification_id_list) {
                            const notification = state.list.find(x => x.id === id)
                            if (notification.activity &&
                                notification.related_type === 'task' &&
                                notification.is_seen === 0) {
                                const taskIndex = taskState.tasks_list.findIndex(x => x.id === notification.related_id)
                                if (taskIndex >= 0) {
                                    if (notification.activity.type === 'task_comment') {
                                        if (taskState.tasks_list[taskIndex].unread_internal_comments) {
                                            taskState.tasks_list[taskIndex].unread_internal_comments -= 1
                                        }
                                    } else if (PUBLIC_MESSAGE_TYPES.includes(notification.activity.type)) {
                                        if (taskState.tasks_list[taskIndex].unread_public_messages) {
                                            taskState.tasks_list[taskIndex].unread_public_messages -= 1
                                        }
                                    }
                                }
                            }
                        }
                    } else {
                        for (const index in taskState.tasks_list) {
                            taskState.tasks_list[index].unread_public_messages = 0
                            taskState.tasks_list[index].unread_internal_comments = 0
                        }
                    }

                    if (notification_id_list && notification_id_list.length) {
                        state.notifications_log_list = state.notifications_log_list.map(x => {
                            if (notification_id_list.includes(x.id)) {
                                x.is_seen = 1
                                x.is_alerted = 1
                            }

                            return x
                        })
                        state.latestNotifications = state.latestNotifications.filter(x => !notification_id_list.includes(x.id))
                    }

                    resolve(resp.data);
                })
                .catch(ex => reject(ex));
        });
    },
    async markNotificationsAlerted() {
        await axios.post('/notifications_alerted')
    },
    fetchNotifications(count = 50) {
        return new Promise(function (resolve, reject) {
            axios
                .get(Vue.config.url_prefix + "/api/notifications/", { params: { count }})
                .then(resp => {
                    state.notifications_log_list = resp.data.notifications;
                    state.list = resp.data.notifications
                    state.latestStickyNotifications = resp.data.sticky_notifications
                    resolve(resp.data);
                })
                .catch(ex => reject(ex));
        });
    },
    markAllNotificationsSeen() {
        actions.markNotificationsSeen();
    },
    navigateToNotificationSource(notification) {
        const invoice_activity_types = [
            "invoice_calculated", "invoice_calculate_failed",
            "invoice_created", "invoice_create_failed",
            "invoice_sent", "invoice_send_failed",
            "invoice_exported", "invoice_export_failed"
        ];
        const company_account_types = [
            "company_account_deleted",
            "trial_time_ending"
        ];
        const client_template_activity_types = ["task_template_assigned", "new_template_task"];

        if (notification.url) {
            router.push(notification.url)
        } else if (notification.activity.related_type === "account") {
            if (invoice_activity_types.includes(notification.activity.type)) {
                router.push({ name: "billing", query: { account_id: notification.activity.related_id }});
            } else if (company_account_types.includes(notification.activity.type)) {
                router.push({name: "settings_profile"});
            } else if (notification.activity.type === 'summary_info_import_failed') {
                router.push({name: "app_store", params: {accountId: notification.activity.related_id}});
            } else if(notification.activity.type === 'update_invoices_paid_sum_failed') {
                const d = new Date()
                d.setMonth(d.getMonth()-3)
                router.push({name: "billing", query: { account_id: notification.activity.related_id,  invoice_status: '', dates_list: d.formatSysDate() + ',' + new Date().formatSysDate()} })
            } else {
                router.push({ name: "users_list" });
            }

        } else if (notification.activity.related_type === "client") {
            if (client_template_activity_types.includes(notification.activity.type)) {
                router.push({ name: "client_profile_plan", params: { id: notification.activity.related_id }});
            } else if (notification.activity.type === 'email_send_failed') {
                const email_log_id = notification.activity._data ? notification.activity._data.email_log_id : null

                router.push({
                    name: "client_profile_communication",
                    params: { id: notification.activity.related_id },
                    query: { email_log_id }
                });
            }
        } else if (notification.activity.related_type === "import") {
            router.push({ name: "import_result", params: { id: notification.activity.related_id }});
        } else if (notification.activity.related_type === "task") {
            taskActions.openTask(notification.activity.related_id);
        } else if (notification.activity.related_type === "calendar") {
            router.push({ name: "profile_vacations", params: {
                personId: notification.activity._data.person_id,
                companyId: notification.activity.account_id
            }});
        } else if(notification.activity.type === "import_finished") {
            router.push({
                name: "clients_list",
                query: {
                    account_id: notification.activity.account_id,
                    client_last_import: 1
                }
            });
        } else {
            console.warn("Unknown notification activity", notification);
        }
    },
    hideNotifications() {
        const hideUntil = new Date()
        hideUntil.setMinutes(hideUntil.getMinutes() + 60)

        state.hideNotificationsUntil = hideUntil
    }
};
export const getters = {
    notifications: computed(() => {
        return state.list;
    }),
    notifications_log: computed(() => {
        return state.notifications_log_list;
    }),
    unseen_notifications: computed(() => state.list.filter(notification => notification && notification.is_seen === 0)),
    isStickyNotification: notification => {
        // sticky activity notification types
        return ["task_created", "task_assigned", "email_send_failed", "request_created", 'calendar_created', 'task_message', 'summary_info_import_failed'].includes(
            notification.activity.type
        );
    },
    latestStickyNotifications: computed(() => {
        if (state.hideNotificationsUntil && state.hideNotificationsUntil > state.currentTime) {
            return []
        }

        return state.latestStickyNotifications
    })
};
export const key = Symbol("NotificationModule");

export const provideNotification = () =>
    provide(key, {
        ...toRefs(state),
        ...getters,
        ...actions
    });

appState.web_socket_connectors.push(actions.connectWs);

export default () => inject(key);
